package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initStaleAotCacheOnJdk25Page(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/StaleAotCacheOnJdk25.md", FrontMatterElement.Builder { addScalar("title", "OpenJDK 25.0.0–25.0.3 uses a stale AOT cache without saying so"); addScalar("searchTitle", "JDK-8377932: Leyden AOT cache accepted after the jar changed, JDK 25.0.0–25.0.3 and 26.0.0–26.0.1"); addScalar("date", "2026-09-07"); addScalar("library", "zavarnik"); addScalar("description", "On JDK 25 before 25.0.4 and JDK 26 before 26.0.2 the JVM does not compare an AOT cache with the jars it was trained against: replace a jar, keep the cache, and the old classes run. The experiment, the logs, and what to check yourself."); }))
}

@Page("/blog/stale-aot-cache-on-jdk25")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun StaleAotCacheOnJdk25Page() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Project Leyden's AOT cache (JEP 483 and 514) records the classes a training run loaded and serves them at the next start, and the JVM is supposed to refuse the cache when the jars on the classpath are no longer the ones it was trained against. On the first four builds of JDK 25 and the first two of JDK 26 it does not: a cache trained with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
        }
        org.jetbrains.compose.web.dom.Text(" is accepted after the jar was touched, replaced or moved in the classpath, and the classes come from the cache. This is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://bugs.openjdk.org/browse/JDK-8377932") {
            org.jetbrains.compose.web.dom.Text("JDK-8377932")
        }
        org.jetbrains.compose.web.dom.Text(", reported and fixed by Ioi Lam of the Leyden team in February 2026 and backported to 25.0.4 and 26.0.2. The bug is not my finding. What is mine is the measurement: which builds ship it, what exactly is accepted, and that ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
        }
        org.jetbrains.compose.web.dom.Text(" — the mode that is supposed to make a rejected cache fatal — does not catch it. I ran into it while building ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(", a Gradle plugin that trains and verifies these caches; the experiment and the logs live there.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-check-is-meant-to-do") }) {
        org.jetbrains.compose.web.dom.Text("What the check is meant to do")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For every jar on the application classpath the cache stores the size and the modification time. At start-up the JVM compares both with the file it finds; a mismatch prints a warning and disables the cache:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""[warning][aot] This file is not the one used while building the AOT cache: 'lib/app.jar', timestamp has changed, size has changed
[info][class,path] Archived app classpath validation: failed
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is the whole protection against running last week's classes on top of this week's jars. Under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
        }
        org.jetbrains.compose.web.dom.Text(" a rejected cache is fatal, exit code 1; in the default mode it is three lines on stderr and a normal start without the cache.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-experiment") }) {
        org.jetbrains.compose.web.dom.Text("The experiment")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik/tree/main/experiments/aot-validation") {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("experiments/aot-validation/run.sh")
            }
        }
        org.jetbrains.compose.web.dom.Text(" compiles a one-class application, trains a cache with the one-step workflow, and then starts the application under sixteen conditions — relocated directory, touched jar, replaced jar, extra classpath entries, an agent, a different GC. It asserts nothing. Every case ends with one line: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("shared")
        }
        org.jetbrains.compose.web.dom.Text(" is how many classes the JVM reported as loaded from the cache, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("file")
        }
        org.jetbrains.compose.web.dom.Text(" whether the application class came from the jar instead. The log is the result, and one log per JDK build is committed under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("results/")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same script, same day, two builds. OpenJDK 25.0.2 on macOS:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""=== R4 jar replaced by a different jar, same name
[info][class,load] App source: shared objects file
shared=895 file=0

=== R14 -XX:AOTMode=on with a replaced jar: exit code
exit=0
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("OpenJDK 25.0.4 on Linux:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""=== R4 jar replaced by a different jar, same name
[warning][aot       ] This file is not the one used while building the AOT cache: 'lib/app.jar', timestamp has changed, size has changed
[info   ][class,path] Archived app classpath validation: failed
shared=0 file=0

=== R14 -XX:AOTMode=on with a replaced jar: exit code
exit=1
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In R4 the jar named ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("app.jar")
        }
        org.jetbrains.compose.web.dom.Text(" no longer contains the class ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("App")
        }
        org.jetbrains.compose.web.dom.Text(" at all — it is a different jar copied over the same name. 25.0.2 starts the application anyway, with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("App")
        }
        org.jetbrains.compose.web.dom.Text(" served from the cache, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
        }
        org.jetbrains.compose.web.dom.Text(", the mode whose purpose is to make a rejected cache fatal, exits 0. The line ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Archived app classpath validation")
        }
        org.jetbrains.compose.web.dom.Text(" never appears in the 25.0.2 log: the check did not fail, it did not run. A touched jar (R3) and a jar moved to the front of the classpath (R6) behave the same way. JetBrains Runtime 25.0.4.1 on the same Mac and OpenJDK 26.0.2.1 on the same Linux box reject all three, so this is the build, not the platform.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-the-check-does-not-run") }) {
        org.jetbrains.compose.web.dom.Text("Why the check does not run")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The validation is guarded by a condition in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotClassLocation.hpp")
        }
        org.jetbrains.compose.web.dom.Text(": it runs only when the highest classpath index that was actually ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("used")
        }
        org.jetbrains.compose.web.dom.Text(" during training reaches the application classpath. In the one-step workflow (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
        }
        org.jetbrains.compose.web.dom.Text(") the cache is assembled by a second JVM after the training run exits, and that JVM loads the classes from the recorded configuration rather than through the class loader path that updates this index. The index stays below the application entries, the condition is false, and the JVM never looks at the jars. The fix (")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/openjdk/jdk/pull/29728") {
            org.jetbrains.compose.web.dom.Text("openjdk/jdk#29728")
        }
        org.jetbrains.compose.web.dom.Text(", merged 16 February 2026) carries the index over from the training run when the final archive is dumped. It reached the 25u repository on 16 March 2026 and is in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jdk-25.0.4-ga")
        }
        org.jetbrains.compose.web.dom.Text(", not in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jdk-25.0.3-ga")
        }
        org.jetbrains.compose.web.dom.Text("; in 26u it is in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jdk-26.0.2-ga")
        }
        org.jetbrains.compose.web.dom.Text(", not in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jdk-26.0.1-ga")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Line")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Check runs")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Builds")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JDK 25")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25.0.0, 25.0.1, 25.0.2, 25.0.3")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JDK 25")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("yes")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25.0.4 and later")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JDK 26")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("26.0.0, 26.0.1")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JDK 26")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("yes")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("26.0.2 and later")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every GA build of JDK 25 from its release in September 2025 until 25.0.4 on 21 July 2026 is on the first row — ten months.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-costs-in-a-deployment") }) {
        org.jetbrains.compose.web.dom.Text("What it costs in a deployment")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The failure is quiet by construction. A cache is trained on CI, shipped next to the jars, and nobody retrains it after a hotfix that changes one jar — or the cache is trained once and reused across builds because it \"still works\". On an affected build the application starts, reports no error under any ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode")
        }
        org.jetbrains.compose.web.dom.Text(", and runs the archived version of every class the cache holds, which on a Ktor service is all of them: on a fixed build, where the count is visible, 2322 of 2322 application, Ktor, coroutines and stdlib classes came from the cache on the stand I measured (JDK 25.0.4, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xlog:class+load")
        }
        org.jetbrains.compose.web.dom.Text("). The new jar is on the classpath and unused. A cache that is silently stale is worse than no cache.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-to-check-yourself") }) {
        org.jetbrains.compose.web.dom.Text("What to check yourself")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Move to 25.0.4 or 26.0.2. Until then, a cache has to be verified against the jars by something other than the JVM. zavarnik does two things about it: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotTrain")
        }
        org.jetbrains.compose.web.dom.Text(" writes ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("lib/app.aot.jars")
        }
        org.jetbrains.compose.web.dom.Text(", the SHA-256 of every jar the cache was trained against, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotVerify")
        }
        org.jetbrains.compose.web.dom.Text(" compares the jars with that manifest before it starts the application at all, on every JDK. A cache shipped next to jars it was not trained against — the case this post is about — fails ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotVerify")
        }
        org.jetbrains.compose.web.dom.Text(" with the jar's name in the message:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""zavarnik: the jars are not the ones app.aot was trained against:
    - lib/config-1.4.9.jar: changed since aotTrain
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Inside a Gradle build the same divergence is closed earlier and silently rather than reported: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotVerify")
        }
        org.jetbrains.compose.web.dom.Text(" runs on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("check")
        }
        org.jetbrains.compose.web.dom.Text(" behind ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotTrain")
        }
        org.jetbrains.compose.web.dom.Text(", so editing a source file retrains the cache and the build passes. The manifest is what stands between a cache that has already been shipped and the jars beside it. The plugin also warns at configuration time when the toolchain is one of the six builds above.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""plugins {
    application
    id("io.github.youndie.zavarnik") version "0.1.0"
}

zavarnik {
    training {
        readyWhen.url("http://127.0.0.1:8080/health")
        workload { get("http://127.0.0.1:8080/api/items") }
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.0")
        }
        org.jetbrains.compose.web.dom.Text(" is on the ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://plugins.gradle.org/plugin/io.github.youndie.zavarnik") {
            org.jetbrains.compose.web.dom.Text("Gradle Plugin Portal")
        }
        org.jetbrains.compose.web.dom.Text(", so that block is the whole installation; every push to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main")
        }
        org.jetbrains.compose.web.dom.Text(" also publishes a snapshot to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("https://reposilite.kotlin.website/snapshots")
        }
        org.jetbrains.compose.web.dom.Text(", and the README says how to take one.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Without the plugin the same check is a hash of the jars written at training time and compared at start, plus one line in the start-up log that must be present on a fixed build and is absent on an affected one:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""[info][class,path] Archived app classpath validation: passed
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If that line is missing from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xlog:class+path=info")
        }
        org.jetbrains.compose.web.dom.Text(" output, the JVM did not look.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-rejection-no-verification-can-see-added-2026-09-08") }) {
        org.jetbrains.compose.web.dom.Text("A rejection no verification can see — added 2026-09-08")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first cache zavarnik shipped to a cluster was refused there, on a fixed JDK build, for a reason no check on the build machine can reproduce. The JVM records the classpath string it was started with and compares it at the next start. The application's start script had ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("lib/*")
        }
        org.jetbrains.compose.web.dom.Text(" on it, and the JVM expands a wildcard in whatever order the filesystem hands back: Docker's overlay2 on the CI runner that trained the cache put ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-http-cio")
        }
        org.jetbrains.compose.web.dom.Text(" first, the k0s node's containerd put ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("packages-shared-api")
        }
        org.jetbrains.compose.web.dom.Text(" first, and the node's JVM printed")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""[warning][aot] The name of app classpath [1] does not match: expected '/opt/konekt/lib/ktor-http-cio-jvm-3.5.2.jar', got '/opt/konekt/lib/packages-shared-api-jvm-0.1.0-SNAPSHOT.jar'
[error][aot] Unable to map shared spaces
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("and started without the cache. The application was fine; the cache was dead weight. A verification that starts the application where the training ran — which is the only place a build has — sees the same order and passes. So the plugin now refuses a wildcard on the start script's classpath at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("installDist")
        }
        org.jetbrains.compose.web.dom.Text(". With the jars listed the same service runs on its cache: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker start")
        }
        org.jetbrains.compose.web.dom.Text(" to the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" went from 4.4 s to 2.0 s at the median of ten restarts. Its pod is Ready 3 s after the container starts against 11 s for the release before, but that pair is not a clean comparison — the same rollout gave readiness a startup probe every second and took away its five-second initial delay, so part of those eight seconds is the polling and not the cache.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("re-run-2026-09-08") }) {
        org.jetbrains.compose.web.dom.Text("Re-run 2026-09-08")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every case above was run again from scratch on the day this note was added, on the same four builds. It reproduced: 25.0.2 starts on a cache whose jar was replaced (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("shared=895 file=0")
        }
        org.jetbrains.compose.web.dom.Text("), ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
        }
        org.jetbrains.compose.web.dom.Text(" exits 0 there, and the line ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Archived app classpath validation")
        }
        org.jetbrains.compose.web.dom.Text(" appears nowhere in its log; 25.0.4, 26.0.2.1 and JetBrains Runtime 25.0.4.1 reject the same cache; the stand again served 2322 of 2322 application, Ktor, coroutines and stdlib classes from the cache. Corrected in the text: the readiness figures above now name the probe change they ride on, and the sentence on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotVerify")
        }
        org.jetbrains.compose.web.dom.Text(" used to say a rebuilt jar fails the build — it does not, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotTrain")
        }
        org.jetbrains.compose.web.dom.Text(" retrains first.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Measured with the one-step workflow (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
            }
            org.jetbrains.compose.web.dom.Text(") on OpenJDK 25.0.2 (macOS arm64), 25.0.4 (Linux x86_64), JetBrains Runtime 25.0.4.1 (macOS arm64) and OpenJDK 26.0.2.1 (Linux x86_64). The two-step workflow (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTMode=record")
            }
            org.jetbrains.compose.web.dom.Text(" then ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("create")
            }
            org.jetbrains.compose.web.dom.Text(") was not run; the bug report does not restrict itself to one workflow.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Only the application classpath is concerned. The JVM's own ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/modules")
            }
            org.jetbrains.compose.web.dom.Text(" is compared by size on every build, which is why a cache from a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-jdk")
            }
            org.jetbrains.compose.web.dom.Text(" image is refused by the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-jre")
            }
            org.jetbrains.compose.web.dom.Text(" image of the same Temurin build — a different limitation, and a documented one.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The modification time is compared in whole seconds. A jar rebuilt within the same second as the training run, with the same size, passes the check on a fixed build too.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Not verified on Windows.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/aot-validation/")
        }
        org.jetbrains.compose.web.dom.Text(" for the script and the four logs, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-architecture.md")
        }
        org.jetbrains.compose.web.dom.Text(" §1.2 for the source-level trace. The how-to this bug is one section of — what the cache gives a Ktor service and how to train, verify and ship it — is ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service") {
            org.jetbrains.compose.web.dom.Text("Leyden AOT cache for a plain Kotlin/JVM service")
        }
        org.jetbrains.compose.web.dom.Text(". The same repository's second phase — why a bytecode optimiser for Ktor services was measured and not built — is in ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/user-code-share-of-a-ktor-service") {
            org.jetbrains.compose.web.dom.Text("User code is 1–4 % of a Ktor service's CPU")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
